/**
 * useStudentTutorConversationData — les lectures de la conversation avec un tuteur.
 *
 * Port des effets de lecture de `common/messenger/CommonChat.js` (branche élève, hors live) :
 *  - au montage : les notifications « messages » déjà affichées sont retirées (mobile), puis le fil
 *    est lu (`getMessages`) ; une lecture réussie marque le fil lu (`getMarkRead`) ;
 *  - au retour de l'app au premier plan : les mêmes deux gestes ;
 *  - une notification d'un message de CE fil reçue pendant la lecture est retirée une seconde après ;
 *  - à la sortie : le fil est marqué lu.
 * Pas de sondage ni de temps réel : comme aujourd'hui, un message du tuteur reçu pendant que la
 * conversation est ouverte n'apparaît qu'à la relecture suivante (premier plan, ou réouverture).
 *
 * « Voir les messages précédents » : la page suivante (`getPageMessages`, `next_page_id`), tant que
 * la messagerie ne dit pas `final_page`. La pagination est tenue ICI, par fil : le réducteur legacy
 * la range dans un `messageMeta` commun à tous les fils (et y verse aussi la réponse d'un envoi de
 * photo).
 *
 * Écrit à la main plutôt que par `useApiFetch` : la donnée lue va dans le compte (patron A, le
 * réducteur de `getMessages`), et ce crochet n'a besoin que de l'issue (`all_data.meta`, l'échec) et
 * de la garde « pour quel fil » (`routeParams.threadId`) — deux conversations peuvent être montées
 * à la fois (le volet de « Messages » et `messenger` poussé par-dessus).
 */
import * as Notifications from "expo-notifications"
import { useCallback, useEffect, useRef, useState } from "react"
import { AppState, Platform } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle, apiRequest } from "../../../../../../data/redux/actions/api"
import { messengerService } from "../../../../../../data/services/messengerService"
import { MESSAGE_NOTIFICATION_PATH, clearMessageNotifications } from "../../../hooks/messageNotifications"

type MessengerSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    routeParams?: { threadId?: string | number }
    all_data?: { meta?: { next_page_id?: string | number | null; final_page?: boolean } }
}

type Paging = { nextPageId: string | number | null; finalPage: boolean }

const isFor = (slice: MessengerSlice | undefined, threadId: string) =>
    slice?.routeParams?.threadId != null && String(slice.routeParams.threadId) === threadId

const pagingOf = (slice: MessengerSlice | undefined): Paging => ({
    nextPageId: slice?.all_data?.meta?.next_page_id ?? null,
    finalPage: slice?.all_data?.meta?.final_page !== false,
})

export function useStudentTutorConversationData(threadId: string | null) {
    const dispatch = useDispatch()
    // Sans fil (une conversation montée mais cachée), les tranches partagées ne la redessinent pas.
    const readReq = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => (threadId == null ? undefined : (state?.api?.getMessages as MessengerSlice | undefined))
    )
    const pageReq = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => (threadId == null ? undefined : (state?.api?.getPageMessages as MessengerSlice | undefined))
    )

    const [paging, setPaging] = useState<Paging>({ nextPageId: null, finalPage: true })
    const [error, setError] = useState<unknown>(null)
    const [loadingEarlier, setLoadingEarlier] = useState(false)
    const awaitingReadRef = useRef(false)
    const awaitingPageRef = useRef(false)

    const markRead = useCallback(() => {
        if (threadId == null) return
        dispatch(apiRequest(messengerService.getMarkRead, { threadId }) as never)
    }, [dispatch, threadId])

    const read = useCallback(() => {
        if (threadId == null) return
        awaitingReadRef.current = true
        setError(null)
        dispatch(apiRequest(messengerService.getMessages, { threadId }) as never)
    }, [dispatch, threadId])

    const loadEarlier = useCallback(() => {
        // Comme l'ancien fil : rien tant qu'une lecture court, ni après la dernière page.
        if (threadId == null || awaitingPageRef.current || awaitingReadRef.current) return
        if (paging.finalPage || paging.nextPageId == null) return
        awaitingPageRef.current = true
        setLoadingEarlier(true)
        dispatch(apiRequest(messengerService.getPageMessages, { threadId, pageId: paging.nextPageId }) as never)
    }, [dispatch, threadId, paging])

    // La lecture du fil : une issue par demande, pour CE fil.
    useEffect(() => {
        if (threadId == null || !awaitingReadRef.current || !isFor(readReq, threadId)) return
        if (readReq?.success) {
            awaitingReadRef.current = false
            setPaging(pagingOf(readReq))
            dispatch(apiIdle(messengerService.getMessages) as never)
            markRead()
        } else if (readReq?.error) {
            awaitingReadRef.current = false
            setError(readReq.error)
            dispatch(apiIdle(messengerService.getMessages) as never)
        }
    }, [dispatch, markRead, threadId, readReq])

    // Les messages plus anciens. Un échec rend le bouton : l'ancien fil ne disait rien de plus.
    useEffect(() => {
        if (threadId == null || !awaitingPageRef.current || !isFor(pageReq, threadId)) return
        if (!pageReq?.success && !pageReq?.error) return
        awaitingPageRef.current = false
        setLoadingEarlier(false)
        if (pageReq.success) setPaging(pagingOf(pageReq))
        dispatch(apiIdle(messengerService.getPageMessages) as never)
    }, [dispatch, threadId, pageReq])

    useEffect(() => {
        if (threadId == null) return
        clearMessageNotifications()
        setPaging({ nextPageId: null, finalPage: true })
        read()

        const appStateRef = { current: AppState.currentState }
        const appState = AppState.addEventListener("change", (next) => {
            if (/inactive|background/.test(appStateRef.current) && next === "active") {
                clearMessageNotifications()
                read()
            }
            appStateRef.current = next
        })

        const received =
            Platform.OS === "web"
                ? null
                : Notifications.addNotificationReceivedListener((notification) => {
                      const data = notification.request.content.data as
                          { path?: string; data?: { thread_id?: string | number } } | undefined
                      if (data?.path !== MESSAGE_NOTIFICATION_PATH || String(data?.data?.thread_id) !== threadId) return
                      setTimeout(() => {
                          void Notifications.dismissNotificationAsync(notification.request.identifier)
                      }, 1000)
                  })

        return () => {
            appState.remove()
            received?.remove()
            awaitingReadRef.current = false
            awaitingPageRef.current = false
            setLoadingEarlier(false)
            dispatch(apiIdle(messengerService.getMessages) as never)
            dispatch(apiIdle(messengerService.getPageMessages) as never)
            markRead()
        }
    }, [dispatch, threadId, read, markRead])

    return {
        error,
        hasEarlier: !paging.finalPage && paging.nextPageId != null,
        loadingEarlier,
        loadEarlier,
        retry: read,
    }
}
